// T-025 — wiring. The sync engine is the singleton here. It observes
// the auth service for sign-in / sign-out (which sets the owner and
// starts / stops the run), reads `navigator.onLine` for the offline
// status, and listens for the `online` and `visibilitychange` events
// per ADR-0003 D8.

import { getAuthService } from "./auth";
import { createPocketBaseTransport, type ServerRecord, type Transport } from "./pocketbase";
import { SyncManager, type Clock } from "./syncManager";

/** A wall-clock clock. Tests inject a fake. */
const realClock: Clock = {
  nowIso(): string {
    return new Date().toISOString();
  },
  setTimeout(cb: () => void, ms: number): unknown {
    return setTimeout(cb, ms);
  },
  clearTimeout(handle: unknown): void {
    clearTimeout(handle as ReturnType<typeof setTimeout>);
  },
  advance(_ms: number): void {
    /* no-op in production */
  },
};

/** In production, the transport is the real PocketBase client. Tests
 *  inject a fake. The wiring module owns the transport so the
 *  SyncManager never imports the SDK directly (ADR-0003 D11). */
function buildTransport(): Transport {
  return createPocketBaseTransport();
}

class SyncEngine {
  #mgr: SyncManager;
  #boundOwner: string | null = null;
  #disposers: Array<() => void> = [];

  constructor(transport: Transport, clock: Clock) {
    this.#mgr = new SyncManager({
      transport,
      clock,
      // The Dexie table is `workouts`; the server collection is
      // `workout_logs`. The SyncManager iterates over `collections` as
      // the server name. A future task can add per-collection routing.
      collections: ["workout_logs", "log_entries", "progress_photos"],
      debounceMs: 2000,
    });

    // ADR-0003 D8: online and visibility triggers.
    if (typeof window !== "undefined") {
      const onOnline = () => {
        if (this.#boundOwner != null) void this.#mgr.syncNow();
      };
      const onVisible = () => {
        if (document.visibilityState === "visible" && this.#boundOwner != null) {
          void this.#mgr.syncNow();
        }
      };
      window.addEventListener("online", onOnline);
      document.addEventListener("visibilitychange", onVisible);
      this.#disposers.push(() => window.removeEventListener("online", onOnline));
      this.#disposers.push(() => document.removeEventListener("visibilitychange", onVisible));
    }

    // Wire to the auth service: when the user changes, set the owner
    // and trigger an initial sync on sign-in; on sign-out, clear the
    // owner and stop. The local Dexie is already wiped on sign-out by
    // the AuthProvider (ADR-0007 D2a), so the engine has nothing left
    // to push or pull.
    const auth = getAuthService();
    const offAuth = auth.subscribe((user) => {
      if (user) {
        this.#boundOwner = user.id;
        void this.#mgr.syncNow();
      } else {
        this.#boundOwner = null;
      }
    });
    this.#disposers.push(offAuth);
  }

  get manager(): SyncManager {
    return this.#mgr;
  }

  /** Pass-through so the AuthProvider (or tests) can call the same
   *  `setOwner` flow without reaching into the DB module. The owner
   *  is the cached value the auth subscription would set; we keep them
   *  in sync so a manual call from the test seam and the auth
   *  subscription can't disagree. */
  setOwner(owner: string | null): void {
    this.#boundOwner = owner;
  }

  /** Free the engine's resources. Called by tests. */
  dispose(): void {
    for (const d of this.#disposers) d();
    this.#disposers = [];
    this.#mgr.dispose();
  }
}

let _engine: SyncEngine | null = null;

/** The singleton sync engine. Constructed lazily so the PocketBase
 *  transport is not created during a unit test. */
export function getSync(): SyncEngine {
  if (_engine) return _engine;
  _engine = new SyncEngine(buildTransport(), realClock);
  return _engine;
}

/** Direct access to the SyncManager, for tests and for the brief's
 *  "the engine module exports a singleton" wiring test. */
export const sync = {
  get status() {
    return getSync().manager.status;
  },
  syncNow(): Promise<void> {
    return getSync().manager.syncNow();
  },
  failedEntries() {
    return getSync().manager.failedEntries();
  },
  retry(id: string) {
    return getSync().manager.retry(id);
  },
  discard(id: string) {
    return getSync().manager.discard(id);
  },
  /** F-7: dispose the sync engine on sign-out so no late retries
   *  fire against the deleted db. */
  dispose() {
    getSync().dispose();
  },
};

/** Re-export the server-side record type for test seams. */
export type { ServerRecord };
